// Single source of truth for the photo-submission fee, in cents. The wizard
// (which decides whether to insert the Pay step) and the gallery (which decides
// whether a failed submission means "money captured" → reconciliation) must
// agree on paid-vs-free. They previously computed it two different ways — the
// wizard parsed the amount numerically while the gallery used string
// truthiness — so an enabled fee of '$0' looked free to the wizard but paid to
// the gallery. Both now derive from this helper.
//
// Minimal structural input so this stays a dependency-free, unit-testable pure
// function (no coupling to the full Ballot type).
export interface SubmissionFeeSource {
  ballot_settings?: {
    submission_fee_settings?: {
      enabled?: boolean
      amount?: string
    }
  }
}

/** Parsed submission fee in whole cents; 0 when disabled, blank, invalid, or non-positive. */
export const getSubmissionFeeCents = (ballot: SubmissionFeeSource | null | undefined): number => {
  const feeSettings = ballot?.ballot_settings?.submission_fee_settings
  if (!feeSettings?.enabled) return 0
  // Keep only digits, dot, and minus so currency symbols and thousands
  // separators (e.g. "$1,000.50") aren't mis-parsed.
  const raw = (feeSettings.amount || '').replace(/[^0-9.-]/g, '')
  // Reject negatives, malformed values (e.g. "1.2.3" — multiple dots), and
  // anything without a digit, so a bogus config becomes 0 rather than a
  // silently mis-parsed amount. Allow at most one decimal point.
  if (raw.includes('-') || !/^\d*\.?\d*$/.test(raw) || !/\d/.test(raw)) return 0
  // Parse whole cents from the string rather than Math.round(dollars * 100),
  // which is subject to float error (1.005 * 100 === 100.49999… → 100, not 101).
  const [wholePart, fracPart = ''] = raw.split('.')
  const frac3 = fracPart.padEnd(3, '0').slice(0, 3)
  let cents = parseInt(wholePart || '0', 10) * 100 + parseInt(frac3.slice(0, 2), 10)
  if (parseInt(frac3[2], 10) >= 5) cents += 1 // round the third decimal to the nearest cent
  return cents > 0 ? cents : 0
}

/** Whether this ballot charges a submission fee (a positive parsed amount). */
export const isPaidSubmissionBallot = (ballot: SubmissionFeeSource | null | undefined): boolean =>
  getSubmissionFeeCents(ballot) > 0
